/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, NavLink } from 'react-router-dom';
import ReactDOM from 'react-dom';
import {
  Bell, LayoutDashboard, Scan, FileSearch, Settings,
  Webhook, ChevronDown, User, CreditCard, LogOut, Flag,
  KeySquare, FileJson, Users, Shield, ChevronRight, Menu, X,
  Folder, EyeOff, SearchCode, GitBranch, Link as LinkIcon,
  Megaphone, Code, SendToBack, ScanText, FileX, Brain, Gauge
} from 'lucide-react';
import { authenticatedFetch } from './utils/api';
import NotificationsPanel from './components/NotificationsPanel';
import SelectRunnerModal from './components/SelectRunnerModal';
import { AnimatePresence } from 'framer-motion';
import ProductUpdatesPanel from './components/ProductUpdatesPanel';
import { useNotification } from './contexts/NotificationContext';
import { useIntelligentPolling } from './hooks/useIntelligentPolling';
import OnboardingModal from './components/OnboardingModal';
import { useTeam } from './contexts/TeamContext';
import FindingDetailsPanel from './components/FindingDetailsPanel';
import OnboardingGuide from './components/OnboardingGuide';
import AnimatedLogo from './components/AnimatedLogo';

const Layout = ({ children, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [username, setUsername] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSelectRunnerModal, setShowSelectRunnerModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const settingsMenuRef = useRef(null);
  const adminMenuRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const [notificationPosition, setNotificationPosition] = useState({ top: 0, right: 0 });
  const { addNotification } = useNotification();
  const [runners, setRunners] = useState([]);
  const [showProductUpdates, setShowProductUpdates] = useState(false);
  const [hasNewUpdate, setHasNewUpdate] = useState(false);
  const productUpdatesRef = useRef(null);
  const productUpdatesButtonRef = useRef(null);
  const [productUpdatesPosition, setProductUpdatesPosition] = useState({ top: 0, right: 0 });
  const { currentTeam, switchTeam } = useTeam();

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const [profile, setProfile] = useState({
    username: '',
    plan_type: 'free'
  });

  const [teams, setTeams] = useState([]);
  const { addSuccessNotification } = useNotification();
  const [selectedFinding, setSelectedFinding] = useState(null);

  const fetchRunners = useCallback(async () => {
    try {
      const url = currentTeam
        ? `/runners?team_id=${currentTeam.team_id}`
        : '/runners?personal=true';
      const response = await authenticatedFetch(url);
      if (response) {
        setRunners(response);
      }
    } catch (error) {
      console.error('Error fetching runners:', error);
    }
  }, [currentTeam]);

  const handleContextSwitch = (teamId = null) => {
    switchTeam(teamId);
    setShowProfileMenu(false);
    fetchRunners();
    if (location.pathname.includes('/results/') || location.pathname.includes('/runners/')) {
      navigate('/');
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/notifications/unread');
      if (response?.notifications) {
        setNotifications(response.notifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    try {
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(notificationId)) {
        console.error('Invalid notification ID format');
        return;
      }

      await authenticatedFetch('/notifications/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_ids: [notificationId] })
      });

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const url = currentTeam
        ? `/runners?team_id=${currentTeam.team_id}`
        : '/runners?personal=true';
      const [notificationsRes, runnersRes] = await Promise.all([
        authenticatedFetch('/notifications/unread'),
        authenticatedFetch(url)
      ]);

      if (notificationsRes?.notifications) {
        setNotifications(notificationsRes.notifications);
      }
      if (runnersRes) {
        setRunners(runnersRes);
      }

      return { notifications: notificationsRes?.notifications, runners: runnersRes };
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  }, [currentTeam]);

  useIntelligentPolling(fetchData, {
    baseInterval: 5000,
    maxInterval: 30000,
    backoffRate: 1.5,
    immediate: true,
    enabled: !!username
  });

  useEffect(() => {
    const checkNewUpdates = async () => {
      try {
        const response = await authenticatedFetch('/changelog/latest');
        if (response) {
          setHasNewUpdate(response.timestamp > response.lastRead);
        }
      } catch (err) {
        console.error('Error fetching updates:', err);
      }
    };

    checkNewUpdates();
    const updateInterval = setInterval(checkNewUpdates, 5 * 60 * 1000);
    return () => clearInterval(updateInterval);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await authenticatedFetch('/profile');
        if (data) {
          setProfile(data);
          setUsername(data.username);
          if (data.first_login) {
            setShowOnboarding(true);
            await authenticatedFetch('/profile/mark-onboarded', { method: 'POST' });
          }
          if (!data.first_login && !data.guide_dismissed) {
            setShowGuide(true);
          }
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchTeams = async () => {
      const response = await authenticatedFetch('/teams');
      setTeams(response.teams);
    };
    fetchTeams();
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setShowGuide(true);
  };

  const handleGuideComplete = () => {
    setShowGuide(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) {
        setShowSettingsMenu(false);
      }
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target)) {
        setShowAdminMenu(false);
      }
      if (productUpdatesRef.current && !productUpdatesRef.current.contains(event.target)) {
        setShowProductUpdates(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultsClick = (e) => {
    e.preventDefault();
    setShowSelectRunnerModal(true);
  };

  const handleLogoutClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowProfileMenu(false);
    if (typeof onLogout === 'function') {
      onLogout();
      navigate('/login');
    }
  };

  const handleTeamChange = async (e) => {
    const team = teams.find(t => t.team_id === e.target.value);
    await switchTeam(team?.team_id || null);
    await fetchRunners();

    if (team) {
      addSuccessNotification(`Switched to team space: ${team.name}`);
    } else {
      addSuccessNotification('Switched to personal space');
    }
  };

  const handleViewFinding = async (findingId) => {
    try {
      const response = await authenticatedFetch(`/findings/${findingId}`);
      if (response) {
        const normalizedFinding = {
          ...response,
          finding_id: response.finding_id || response.result_id,
          token_type: response.token_type,
          token_value: response.token_value,
          repository_url: response.repository_url,
          repository_author: response.repository_author,
          raw_url: response.raw_url,
          runner_name: response.runner_name || 'Unknown Runner',
          status: response.status || 'new',
          priority: response.priority,
          notes: response.notes,
          assigned_to_username: response.assigned_to_username,
          created_at: response.created_at,
          details: {
            token_type: response.token_type,
            token_value: response.token_value,
            repository_url: response.repository_url,
            commit: {
              date: response.commit_date,
              author: response.repository_author
            },
            raw_url: response.raw_url
          },
          timestamp: new Date(response.created_at).getTime() / 1000
        };
        setSelectedFinding(normalizedFinding);
        setShowNotifications(false);
      }
    } catch (error) {
      console.error('Error fetching finding:', error);
    }
  };

  const handleUpdateFinding = async (findingId, updates) => {
    try {
      const response = await authenticatedFetch(`/findings/${findingId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
      if (response) {
        setSelectedFinding(prev => ({ ...prev, ...response }));
      }
    } catch (error) {
      console.error('Error updating finding:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotificationIds = notifications.filter(n => !n.read).map(n => n.id);
      if (unreadNotificationIds.length === 0) return;

      await authenticatedFetch('/notifications/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_ids: unreadNotificationIds })
      });

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setShowNotifications(false);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Navigation items
  const mainNavItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/runners', icon: Scan, label: 'Runners' },
    { to: '/results', icon: FileSearch, label: 'Results', onClick: handleResultsClick },
    { to: '/deepscan', icon: ScanText, label: 'Deep Scans' },
    { to: '/action-items', icon: Flag, label: 'Risk Management' },
    { to: '/teams', icon: Users, label: 'Teams' },
  ];

  // Settings items grouped by function
  const settingsGroups = [
    {
      label: 'Alerts & Notifications',
      items: [
        { to: '/settings/webhooks', icon: Webhook, label: 'Webhooks' },
        { to: '/settings/notifications', icon: Bell, label: 'Notifications' },
        { to: '/settings/notification-workflows', icon: GitBranch, label: 'Notification Workflows' },
        { to: '/settings/integrations', icon: LinkIcon, label: 'Integrations' },
      ]
    },
    {
      label: 'Filtering & Exclusions',
      items: [
        { to: '/settings/tokens', icon: SearchCode, label: 'Detection Patterns' },
        { to: '/settings/hidden-repositories', icon: Folder, label: 'Hidden Repositories' },
        { to: '/settings/ignored-extensions', icon: FileX, label: 'Ignored Extensions' },
      ]
    }
  ];

  // Flat list for mobile menu and route matching
  const settingsItems = settingsGroups.flatMap(group => group.items);

  const adminItems = [
    { to: '/admin/users', icon: Users, label: 'Users' },
    { to: '/admin/patterns', icon: Code, label: 'Patterns' },
    { to: '/admin/false-positive-reports', icon: Flag, label: 'FP Reports' },
    { to: '/admin/llm-config', icon: Brain, label: 'LLM Config' },
    { to: '/admin/product-updates', icon: Megaphone, label: 'Product Updates' },
    { to: '/admin/quotas', icon: Gauge, label: 'Quotas' },
  ];

  const isActive = (path) => {
    if (path === '/deepscan') {
      return location.pathname === path || location.pathname.startsWith('/deepscan/');
    }
    return location.pathname === path;
  };
  const isSettingsActive = () => location.pathname.startsWith('/settings');
  const isAdminActive = () => location.pathname.startsWith('/admin');

  return (
    <div className="min-h-screen bg-surface">
      {/* Top Navigation */}
      <header className="sticky top-0 z-[100] bg-surface-secondary border-b border-border">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Left: Logo + Main Nav */}
            <div className="flex items-center gap-8">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2">
                <AnimatedLogo src="/logo192.png" alt="GitGraber" size={28} />
                <span className="font-semibold text-ink text-lg hidden sm:block">GitGraber</span>
                <span className="text-tiny text-accent font-medium hidden sm:block">Beta</span>
              </Link>

              {/* Main Navigation - Desktop */}
              <nav className="hidden lg:flex items-center gap-1">
                {mainNavItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={item.onClick}
                    className={`flex items-center gap-2 px-3 py-2 text-caption font-medium rounded transition-base ${isActive(item.to)
                        ? 'bg-accent-soft text-accent'
                        : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary'
                      }`}
                  >
                    <item.icon size={16} />
                    <span>{item.label}</span>
                    {item.isNew && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-accent text-white rounded">
                        New
                      </span>
                    )}
                  </Link>
                ))}

                {/* Settings Dropdown */}
                <div className="relative" ref={settingsMenuRef}>
                  <button
                    onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                    className={`flex items-center gap-2 px-3 py-2 text-caption font-medium rounded transition-base ${isSettingsActive()
                        ? 'bg-accent-soft text-accent'
                        : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary'
                      }`}
                  >
                    <Settings size={16} />
                    <span>Settings</span>
                    <ChevronDown size={14} className={`transition-transform ${showSettingsMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {showSettingsMenu && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-surface-secondary border border-border rounded-md shadow-dropdown py-1">
                      {settingsGroups.map((group, groupIndex) => (
                        <div key={group.label}>
                          {groupIndex > 0 && (
                            <div className="my-1 border-t border-border" />
                          )}
                          <p className="px-4 py-1.5 text-[10px] font-semibold text-ink-tertiary uppercase tracking-wider">
                            {group.label}
                          </p>
                          {group.items.map((item) => (
                            <Link
                              key={item.to}
                              to={item.to}
                              onClick={() => setShowSettingsMenu(false)}
                              className={`flex items-center gap-3 px-4 py-2 text-caption ${isActive(item.to)
                                  ? 'bg-accent-soft text-accent'
                                  : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary'
                                }`}
                            >
                              <item.icon size={16} />
                              <span>{item.label}</span>
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Admin Dropdown */}
                {profile.role === 'admin' && (
                  <div className="relative" ref={adminMenuRef}>
                    <button
                      onClick={() => setShowAdminMenu(!showAdminMenu)}
                      className={`flex items-center gap-2 px-3 py-2 text-caption font-medium rounded transition-base ${isAdminActive()
                          ? 'bg-accent-soft text-accent'
                          : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary'
                        }`}
                    >
                      <Shield size={16} />
                      <span>Admin</span>
                      <ChevronDown size={14} className={`transition-transform ${showAdminMenu ? 'rotate-180' : ''}`} />
                    </button>

                    {showAdminMenu && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-surface-secondary border border-border rounded-md shadow-dropdown py-1">
                        {adminItems.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setShowAdminMenu(false)}
                            className={`flex items-center gap-3 px-4 py-2.5 text-caption ${isActive(item.to)
                                ? 'bg-accent-soft text-accent'
                                : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary'
                              }`}
                          >
                            <item.icon size={16} />
                            <span>{item.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </nav>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {/* Team Selector */}
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-surface-tertiary border border-border rounded text-caption">
                <SendToBack size={14} className="text-ink-secondary" />
                <select
                  value={currentTeam?.team_id || ''}
                  onChange={handleTeamChange}
                  className="bg-transparent text-ink focus:outline-none cursor-pointer"
                >
                  <option value="">Personal</option>
                  {teams.map(team => (
                    <option key={team.team_id} value={team.team_id}>{team.name}</option>
                  ))}
                </select>
              </div>

              {/* Product Updates */}
              <div className="relative" ref={productUpdatesRef}>
                <button
                  ref={productUpdatesButtonRef}
                  onClick={async () => {
                    if (productUpdatesButtonRef.current) {
                      const rect = productUpdatesButtonRef.current.getBoundingClientRect();
                      const panelWidth = 384; // w-96 = 384px
                      const spacing = 8;

                      // Calculer la position à droite de l'icône
                      let right = window.innerWidth - rect.right;

                      // Si le panel dépasse à droite, l'aligner à droite de l'icône
                      if (right < panelWidth) {
                        right = window.innerWidth - rect.left - panelWidth - spacing;
                        // Si ça dépasse toujours à gauche, le centrer ou l'aligner à droite de l'écran
                        if (right < 0) {
                          right = 16; // Marge de 16px depuis le bord droit
                        }
                      }

                      setProductUpdatesPosition({
                        top: rect.bottom + spacing,
                        right: right
                      });
                    }
                    setShowProductUpdates(!showProductUpdates);
                    if (hasNewUpdate) {
                      setHasNewUpdate(false);
                      try {
                        await authenticatedFetch('/changelog/latest/mark-read', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ timestamp: Date.now() })
                        });
                      } catch (err) {
                        setHasNewUpdate(true);
                      }
                    }
                  }}
                  className="relative p-2 text-ink-secondary hover:text-ink hover:bg-surface-tertiary rounded transition-base"
                >
                  <Megaphone size={18} />
                  {hasNewUpdate && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full" />
                  )}
                </button>
                <AnimatePresence>
                  {showProductUpdates && (
                    <ProductUpdatesPanel
                      onClose={() => setShowProductUpdates(false)}
                      position={productUpdatesPosition}
                    />
                  )}
                </AnimatePresence>
              </div>

              {/* Notifications */}
              <div className="relative">
                <button
                  ref={notificationButtonRef}
                  onClick={() => {
                    if (notificationButtonRef.current) {
                      const rect = notificationButtonRef.current.getBoundingClientRect();
                      const panelWidth = 384; // w-96 = 384px
                      const spacing = 8;

                      // Calculer la position à droite de l'icône
                      let right = window.innerWidth - rect.right;

                      // Si le panel dépasse à droite, l'aligner à droite de l'icône
                      if (right < panelWidth) {
                        right = window.innerWidth - rect.left - panelWidth - spacing;
                        // Si ça dépasse toujours à gauche, le centrer ou l'aligner à droite de l'écran
                        if (right < 0) {
                          right = 16; // Marge de 16px depuis le bord droit
                        }
                      }

                      setNotificationPosition({
                        top: rect.bottom + spacing,
                        right: right
                      });
                    }
                    setShowNotifications(!showNotifications);
                  }}
                  className="relative p-2 text-ink-secondary hover:text-ink hover:bg-surface-tertiary rounded transition-base"
                >
                  <Bell size={18} />
                  {notifications.filter(n => !n.read).length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-accent text-white text-[10px] font-semibold rounded-full flex items-center justify-center px-1">
                      {notifications.filter(n => !n.read).length}
                    </span>
                  )}
                </button>
                {showNotifications && ReactDOM.createPortal(
                  <div
                    className="fixed inset-0 z-[99999]"
                    onClick={(e) => { e.stopPropagation(); setShowNotifications(false); }}
                  >
                    <NotificationsPanel
                      notifications={notifications}
                      onClose={() => setShowNotifications(false)}
                      onMarkAsRead={handleMarkAsRead}
                      onViewFinding={handleViewFinding}
                      onMarkAllAsRead={handleMarkAllAsRead}
                      position={notificationPosition}
                    />
                  </div>,
                  document.body
                )}
              </div>

              {/* Profile */}
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 p-1.5 hover:bg-surface-tertiary rounded transition-base"
                >
                  <img
                    src={`https://api.dicebear.com/6.x/initials/svg?seed=${username}`}
                    alt="Profile"
                    className="w-7 h-7 rounded bg-surface-tertiary"
                  />
                  <ChevronDown size={14} className="text-ink-secondary hidden sm:block" />
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-surface-secondary border border-border rounded-md shadow-dropdown overflow-hidden">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-caption font-medium text-ink">{username}</p>
                      <p className="text-tiny text-ink-tertiary capitalize">{profile?.plan_type || 'Free'} plan</p>
                    </div>

                    <div className="py-1">
                      <Link
                        to="/profile"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-caption text-ink-secondary hover:text-ink hover:bg-surface-tertiary"
                      >
                        <User size={16} />
                        <span>Profile Settings</span>
                      </Link>
                      <Link
                        to="/settings/personal-tokens"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-caption text-ink-secondary hover:text-ink hover:bg-surface-tertiary"
                      >
                        <KeySquare size={16} />
                        <span>Personal Access Tokens</span>
                      </Link>
                      <Link
                        to="/billing"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-caption text-ink-secondary hover:text-ink hover:bg-surface-tertiary"
                      >
                        <CreditCard size={16} />
                        <span>Billing & Plans</span>
                      </Link>
                      <Link
                        to="/api/docs/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-2.5 text-caption text-ink-secondary hover:text-ink hover:bg-surface-tertiary"
                      >
                        <FileJson size={16} />
                        <span>API Documentation</span>
                      </Link>
                    </div>

                    <div className="border-t border-border py-1">
                      <button
                        onClick={handleLogoutClick}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-caption text-danger hover:bg-danger-soft"
                      >
                        <LogOut size={16} />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-ink-secondary hover:text-ink hover:bg-surface-tertiary rounded transition-base"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-surface-secondary">
            <div className="px-4 py-3 space-y-1">
              {mainNavItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={(e) => {
                    if (item.onClick) item.onClick(e);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded ${isActive(item.to)
                      ? 'bg-accent-soft text-accent'
                      : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary'
                    }`}
                >
                  <item.icon size={18} />
                  <span>{item.label}</span>
                  {item.isNew && (
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-accent text-white rounded">
                      New
                    </span>
                  )}
                </Link>
              ))}

              <div className="pt-2 border-t border-border mt-2">
                <p className="px-3 py-2 text-tiny font-medium text-ink-tertiary uppercase tracking-wider">Settings</p>
                {settingsGroups.map((group, groupIndex) => (
                  <div key={group.label}>
                    {groupIndex > 0 && (
                      <div className="my-2 mx-3 border-t border-border" />
                    )}
                    <p className="px-3 py-1.5 text-[10px] font-semibold text-ink-tertiary uppercase tracking-wider">
                      {group.label}
                    </p>
                    {group.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded ${isActive(item.to)
                            ? 'bg-accent-soft text-accent'
                            : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary'
                          }`}
                      >
                        <item.icon size={18} />
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>

              {profile.role === 'admin' && (
                <div className="pt-2 border-t border-border mt-2">
                  <p className="px-3 py-2 text-tiny font-medium text-ink-tertiary uppercase tracking-wider">Admin</p>
                  {adminItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded ${isActive(item.to)
                          ? 'bg-accent-soft text-accent'
                          : 'text-ink-secondary hover:text-ink hover:bg-surface-tertiary'
                        }`}
                    >
                      <item.icon size={18} />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}

              {/* Mobile Team Selector */}
              <div className="pt-2 border-t border-border mt-2">
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <SendToBack size={16} className="text-ink-secondary" />
                  <select
                    value={currentTeam?.team_id || ''}
                    onChange={handleTeamChange}
                    className="flex-1 bg-surface-tertiary border border-border rounded px-3 py-2 text-caption text-ink"
                  >
                    <option value="">Personal Space</option>
                    {teams.map(team => (
                      <option key={team.team_id} value={team.team_id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-4 mt-auto">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
          <p className="text-center text-tiny text-ink-tertiary">
            © {new Date().getFullYear()} GitGraber. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Modals */}
      {showSelectRunnerModal && (
        <SelectRunnerModal onClose={() => setShowSelectRunnerModal(false)} />
      )}

      <AnimatePresence>
        {showOnboarding && <OnboardingModal onComplete={handleOnboardingComplete} />}
      </AnimatePresence>

      <AnimatePresence>
        {showGuide && <OnboardingGuide onComplete={handleGuideComplete} />}
      </AnimatePresence>

      {selectedFinding && (
        <FindingDetailsPanel
          finding={selectedFinding}
          onClose={() => setSelectedFinding(null)}
          onUpdate={handleUpdateFinding}
        />
      )}
    </div>
  );
};

export default Layout;
